import { defaultProjSettings } from 'pages/ProjectsPage/projectUtils'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { indexedDBStorage } from './persistDb'
import { Workspace2Store } from './types/types'

// Track grace period timers to prevent memory leaks
const graceTimerIds = new Map<string, NodeJS.Timeout>()

/**
 * Creates a zustand store for managing workspace state
 * @returns {Workspace2Store} A reactive store with workspace management methods
 */
export const useWorkspace2Store = create<Workspace2Store>()(
    persist(
        (set, get) => ({
            /** initial state */
            isContentTeamFromStudioPage: false,
            projectId: undefined,
            treeData: [],
            selectedProjectElementNode: {},
            selectedProjectElementNodeId: '',
            activeTab: 1,
            articlesLoading: [],
            expandedKeys: [],
            contentItemViewNodes: [],
            isTrackChangesExpanded: false,
            previousContentItemViewNodes: [],
            classificationNodes: [],
            textContentElementToAdd: null,
            isTreeLoading: false,
            isTreeMappingInProgress: false, // New flag for tree mapping operations
            isEditingPlainText: false,
            isBetaWorkspaceEnabled: false, // Initialize beta workspace flag
            isUnderMaintenance: false, // Initialize maintenance mode flag
            lastError: null, // Initialize last error
            projectSettings: defaultProjSettings,
            editorDeactivationSignal: 0,
            familyAddedProductTypes: [],
            treeRef: undefined,
            selectedElementData: {},
            elementsBeingProcessed: new Set<string>(),
            recentlyProcessedElements: new Map<string, number>(),
            bpSyncTrigger: 0,
            contentIdsToReplaceOnNextFetch: [], // Track user-added content IDs to replace after BP sync
            isActionButtonMousedown: false, // Track touchpad clicks on action buttons
            isBPAutomationInProgress: false, // Track if Best Practice automation is currently running

            /** actions/setters */
            actions: {
                setIsContentTeamFromStudioPage: (isContentTeamFromStudioPage) =>
                    set({ isContentTeamFromStudioPage }),
                setProjectId: (projectId) => set({ projectId }),
                setTreeData: (treeData) => set({ treeData }),
                setSelectedProjectElementNode: (selectedProjectElementNode) => {
                    set({ selectedProjectElementNode })

                    set({
                        selectedProjectElementNodeId:
                            selectedProjectElementNode?.id ||
                            selectedProjectElementNode?.parentNodeId ||
                            '',
                    })
                },
                setActiveTab: (activeTab) => set({ activeTab }),
                setArticlesLoading: (articlesLoading) =>
                    set({ articlesLoading }),
                setExpandedKeys: (keys) => set({ expandedKeys: keys }),
                setContentItemViewNodes: (contentItemViewNodes) =>
                    set((state) => ({
                        contentItemViewNodes,
                        editorDeactivationSignal:
                            state.editorDeactivationSignal + 1,
                    })),
                // Does NOT bump editorDeactivationSignal — use only for background
                // reconciliation where active CKEditor instances must stay open
                setContentItemViewNodesSilently: (contentItemViewNodes) =>
                    set({ contentItemViewNodes }),
                setPreviousContentItemViewNodes: (
                    previousContentItemViewNodes
                ) => set({ previousContentItemViewNodes }),
                setClassificationNodes: (classificationNodes) =>
                    set({ classificationNodes }),
                setTextContentElementToAdd: (textContentElementToAdd) =>
                    set({ textContentElementToAdd }),
                switchWorkspace: (enabled) =>
                    set({ isBetaWorkspaceEnabled: enabled }),
                setMaintenanceMode: (error) =>
                    set({
                        isUnderMaintenance: error !== null,
                        lastError: error,
                    }),
                setDefault: () =>
                    set({
                        articlesLoading: ['default'],
                        contentItemViewNodes: [],
                        previousContentItemViewNodes: [],
                        textContentElementToAdd: null,
                        treeData: [], // clear tree data to prevent memory leaks
                    }),
                setIsTrackChangesExpanded: (isTrackChangesExpanded) => {
                    set({ isTrackChangesExpanded })
                },
                setIsTreeLoading: (isTreeLoading: boolean) =>
                    set({ isTreeLoading }),
                setIsTreeMappingInProgress: (
                    isTreeMappingInProgress: boolean
                ) => set({ isTreeMappingInProgress }),
                setProjectSettings: (projectSettings) =>
                    set({ projectSettings }),
                setIsEditingPlainText: (isEditingPlainText) =>
                    set({ isEditingPlainText }),
                deactivateAllEditors: () =>
                    set((state) => ({
                        editorDeactivationSignal:
                            state.editorDeactivationSignal + 1,
                    })),
                setFamilyAddedProductTypes: (familyAddedProductTypes) =>
                    set({ familyAddedProductTypes }),
                setTreeRef: (treeRef) => set({ treeRef }),
                setSelectedElementData: (selectedElementData) =>
                    set({ selectedElementData }),
                markElementAsBeingProcessed: (elementId: string) => {
                    const state = get()
                    const newSet = new Set(state.elementsBeingProcessed)
                    newSet.add(elementId)
                    set({ elementsBeingProcessed: newSet })
                },
                markElementAsRecentlyProcessed: (elementId: string) => {
                    const state = get()
                    const newBeingProcessed = new Set(
                        state.elementsBeingProcessed
                    )
                    const newRecentlyProcessed = new Map(
                        state.recentlyProcessedElements
                    )

                    // Remove from being processed
                    newBeingProcessed.delete(elementId)

                    // Add to recently processed with timestamp
                    newRecentlyProcessed.set(elementId, Date.now())

                    set({
                        elementsBeingProcessed: newBeingProcessed,
                        recentlyProcessedElements: newRecentlyProcessed,
                    })

                    // Clear existing timer if any to prevent memory leaks
                    const existingTimer = graceTimerIds.get(elementId)
                    if (existingTimer) {
                        clearTimeout(existingTimer)
                    }

                    // Clear from recently processed after grace period (2500ms)
                    const timerId = setTimeout(() => {
                        const currentState = get()
                        const updatedRecentlyProcessed = new Map(
                            currentState.recentlyProcessedElements
                        )
                        updatedRecentlyProcessed.delete(elementId)
                        set({
                            recentlyProcessedElements: updatedRecentlyProcessed,
                        })
                        graceTimerIds.delete(elementId)
                    }, 2500)

                    graceTimerIds.set(elementId, timerId)
                },
                triggerBPSyncCheck: () => {
                    set((state) => ({
                        bpSyncTrigger: (state.bpSyncTrigger || 0) + 1,
                    }))
                },
                setIsActionButtonMousedown: (
                    isActionButtonMousedown: boolean
                ) => set({ isActionButtonMousedown }),
                setIsBPAutomationInProgress: (
                    isBPAutomationInProgress: boolean
                ) => set({ isBPAutomationInProgress }),
                setContentIdsToReplaceOnNextFetch: (contentIds: string[]) => {
                    set({ contentIdsToReplaceOnNextFetch: contentIds })
                },
                clearContentIdsToReplaceOnNextFetch: () => {
                    set({ contentIdsToReplaceOnNextFetch: [] })
                },
                isElementBeingProcessed: (elementId: string) => {
                    const state = get()
                    return state.elementsBeingProcessed?.has(elementId) ?? false
                },
                isElementRecentlyProcessed: (elementId: string) => {
                    const state = get()
                    if (!state.recentlyProcessedElements) return false

                    const timestamp = state.recentlyProcessedElements.get(
                        elementId
                    )
                    if (!timestamp) return false

                    // Check if within grace period (2500ms)
                    const elapsed = Date.now() - timestamp
                    return elapsed < 2500
                },
                clearAllGraceTimers: () => {
                    graceTimerIds.forEach((timerId) => clearTimeout(timerId))
                    graceTimerIds.clear()
                    set({
                        elementsBeingProcessed: new Set(),
                        recentlyProcessedElements: new Map(),
                    })
                },
            },
        }),
        {
            name: 'workspace2-store',
            // versioning for future migrations
            version: 1.1,
            storage: indexedDBStorage,
            // partial persistence: explicitly choose which state properties to persist
            partialize: (state) => ({
                contentItemViewNodes: state.contentItemViewNodes,
                classificationNodes: state.classificationNodes,
                expandedKeys: state.expandedKeys,
                selectedProjectElementNodeId:
                    state.selectedProjectElementNodeId,
                activeTab: state.activeTab,
            }),
        }
    )
)

/**
 * A custom hook that retrieves a shallowly selected value from the `useWorkspace2Store` store.
 *
 * Sample usage:
 * ```typescript
 * const { activeTab, isTreeLoading } = useShallowSelector((state) => ({
 *   activeTab: state.activeTab,
 *   isTreeLoading: state.isTreeLoading,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useShallowSelector = <T>(
    selector: (state: Workspace2Store) => T
): T => useWorkspace2Store(useShallow(selector))

/**
 * Zustand selector that determines if the current workspace is from OM/SMM Page.
 * @returns The content team from studio page status from store.
 */
export const useIsContentTeamFromStudioPage = () =>
    useShallowSelector((state) => state.isContentTeamFromStudioPage)
/**
 * Zustand selector that gets the beta workspace status.
 * @returns The beta workspace status from store.
 */
export const useIsBetaWorkspaceEnabled = () =>
    useShallowSelector((state) => state.isBetaWorkspaceEnabled)
/**
 * Zustand selector that gets the maintenance mode status.
 * @returns The maintenance mode status from store.
 */
export const useIsUnderMaintenance = () =>
    useShallowSelector((state) => state.isUnderMaintenance)
/**
 * Zustand selector that gets the last error.
 * @returns The last error from store.
 */
export const useLastError = () => useShallowSelector((state) => state.lastError)
/**
 * Zustand selector that gets the selected project element node.
 * @returns The selected project element node from store.
 */
export const useSelectedProjectElementNode = () =>
    useShallowSelector((state) => state.selectedProjectElementNode)
/**
 * Zustand selector that gets the selected project element node ID.
 * @returns The selected project element node ID from store.
 */
export const useSelectedProjectElementNodeId = () =>
    useShallowSelector((state) => state.selectedProjectElementNodeId)
/**
 * Zustand selector that gets the active tab.
 * @returns The active tab from store.
 */
export const useActiveTab = () => useShallowSelector((state) => state.activeTab)
/**
 * Zustand selector that gets the articles loading state.
 * @returns The articles loading state from store.
 */
export const useArticlesLoading = () =>
    useShallowSelector((state) => state.articlesLoading)
/**
 * Zustand selector that gets the expanded keys.
 * @returns The expanded keys from store.
 */
export const useExpandedKeys = () =>
    useShallowSelector((state) => state.expandedKeys)
/**
 * Zustand selector that gets the content item view nodes.
 * @returns The content item view nodes from store.
 */
export const useContentItemViewNodes = () =>
    useShallowSelector((state) => state.contentItemViewNodes)
/**
 * Zustand selector that gets the text content element currently being added.
 * @returns The text content element currently being added from store.
 */
export const useTextContentElementToAdd = () =>
    useShallowSelector((state) => state.textContentElementToAdd)

/**
 * A custom hook that retrieves the `classificationNodes` state from the `useWorkspace2Store` store.
 *
 * @returns The `classificationNodes` state from the workspace store.
 */
export const useClassificationNodes = () =>
    useShallowSelector((state) => state.classificationNodes)
/**
 * Zustand selector that gets the tree loading state.
 * @returns The tree loading state from store.
 */
export const useIsTreeLoading = () =>
    useShallowSelector((state) => state.isTreeLoading)
/**
 * Zustand selector that gets the current project settings.
 * @returns The project settings from store.
 */
export const useProjectSettings = () =>
    useShallowSelector((state) => state.projectSettings)

/**
 * Zustand selector that gets the selected element data (ProjectUniformatClassification or UniformatClassification).
 * Contains uniformatClassificationProperties and other classification details.
 * @returns The selected element data from store.
 */
export const useSelectedElementData = () =>
    useShallowSelector((state) => state.selectedElementData)

/** Zustand selector that gets the actions/setters.
 *
 * Actions/setters are stable and won't change between re-renders,
 * so we can destructure the specific actions needed.
 *
 * @example
 * ```typescript
 *     const { setSelectedProjectElementNode, ...etc } = useWorkspace2Actions()
 * ```
 */
export const useWorkspace2Actions = () =>
    useShallowSelector((state) => state.actions)
